import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import * as icons from "react-bootstrap-icons";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import { Footer } from "../../Footer";
import { ResultsPager } from "../../CommonComponent/Results/ResultsPager.jsx";
import { usePagedList } from "../../hooks/usePagedList";
import { GetBroadcasts } from "../../redux/auth-reducer/auth-service";
import {
  LIST_LOAD_FAILED_MESSAGE,
  PROGRESS_POLL_MS,
  SEND_BROADCAST_PATH,
  STOPPED_UPDATING_MESSAGE,
  broadcastDetailsPath,
  emailsCell,
  formatSentAt,
  isInProgress,
  listStatusTag,
  playersLabel,
} from "../../utils/helper/broadcasts";
import { BroadcastAlert } from "./BroadcastAlert.jsx";

/** react-data-table-component's default line between rows. */
const ROW_DIVIDER = "rgba(0, 0, 0, .12)";

/**
 * As in the design: a line under every row, the last one included (react-data-table-component
 * leaves it out), and a light blue hover that keeps the lines. The library's own hover paints the
 * row's line white and draws a white outline over its neighbours' lines. Its scrolling wrapper takes
 * the card's rounded corners and clips to them, which would cut the ends off the last line, so only
 * the top corners stay rounded.
 */
const TABLE_STYLES = {
  responsiveWrapper: {
    style: { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
  },
  rows: {
    style: {
      borderBottomStyle: "solid",
      borderBottomWidth: "1px",
      borderBottomColor: ROW_DIVIDER,
    },
    highlightOnHoverStyle: {
      backgroundColor: "#f7f9ff",
      borderBottomColor: ROW_DIVIDER,
      outlineStyle: "none",
    },
  },
};

/**
 * MG-014 / AD-013 (#167, #168): Broadcasting Center - the broadcasts the Manager and their Admins
 * have sent, newest first, shared by the whole team. A row opens Broadcast Details.
 *
 * After Send the page opens with `{ justSentId, sentCount }` in the router state: the confirmation
 * (AC5) and the "Just sent" row come from it, and it is cleared so a reload does not show them again.
 * The emails go out in the background, so while any row on screen is still sending the page reads
 * its page again every few seconds, until its "Sending…" tag becomes a result. A read that gets no
 * answer (no reply or a 5xx) keeps the rows, stops that, and says so; the next good read - a page
 * change, or the user's reload - starts it again.
 */
export const BroadcastingCenter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(false);
  const [justSent, setJustSent] = useState(() =>
    location.state?.justSentId ? { id: location.state.justSentId, count: location.state.sentCount } : null
  );
  // The recipients the confirmation names; null once it is closed.
  const [sentCount, setSentCount] = useState(() =>
    location.state?.justSentId ? location.state.sentCount : null
  );
  const pager = usePagedList(GetBroadcasts);

  function sidebarfun() {
    setData(!data);
  }

  // Keyed on location.key, as in UserSuper.js: only a new arrival applies the state, and it is then
  // cleared - the clearing navigate is itself a location change.
  useEffect(() => {
    const justSentId = location.state?.justSentId;
    if (!justSentId) return;

    setJustSent({ id: justSentId, count: location.state.sentCount });
    setSentCount(location.state.sentCount);
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const anyInProgress = pager.rows.some(isInProgress);
  const { reload } = pager;

  useEffect(() => {
    if (!anyInProgress || pager.loading || pager.failed) return undefined;
    const timer = setTimeout(reload, PROGRESS_POLL_MS);
    return () => clearTimeout(timer);
    // reload is a new function on every render; the poll only needs to restart when the rows change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyInProgress, pager.loading, pager.failed, pager.rows]);

  const openDetails = (row) => navigate(broadcastDetailsPath(row.id));

  const columns = [
    {
      name: <strong>Date and Time Sent</strong>,
      selector: (row) => formatSentAt(row?.sentAt),
      minWidth: "170px",
    },
    {
      name: <strong>Subject</strong>,
      cell: (row) => (
        <span className="broadcast-subject-cell">
          <Link to={broadcastDetailsPath(row.id)} title={row.subject} onClick={(e) => e.stopPropagation()}>
            {row.subject}
          </Link>
          {justSent?.id === row.id && <span className="broadcast-new-tag">Just sent</span>}
        </span>
      ),
      grow: 2,
    },
    {
      name: <strong>Recipients</strong>,
      cell: (row) => {
        const tag = listStatusTag(row);
        return (
          <span className="broadcast-recipients-cell">
            <span className="broadcast-num">{row.recipientCount}</span>
            {tag && <span className={`broadcast-tag ${tag.kind}`}>{tag.label}</span>}
          </span>
        );
      },
      minWidth: "150px",
    },
    {
      name: <strong>Recipient Emails</strong>,
      cell: (row) => {
        const { emails, more } = emailsCell(row.firstEmails, row.recipientCount);
        // One line: the addresses are cut short, never "and X more".
        return (
          <span className="broadcast-emails">
            {emails && <span className="broadcast-emails-list">{emails}</span>}
            {more > 0 && <span className="broadcast-more">{`and ${more} more`}</span>}
          </span>
        );
      },
      grow: 2,
    },
    {
      name: <span className="visually-hidden">Open</span>,
      cell: () => <icons.ChevronRight className="broadcast-go" aria-hidden="true" />,
      width: "56px",
      right: true,
    },
  ];

  const emptyState = (
    <div className="broadcast-empty">
      <icons.EnvelopePaper className="broadcast-empty-icon" aria-hidden="true" />
      <p>
        You haven't sent any broadcasts yet.
        <br />
        Broadcasts you send to your players will be listed here.
      </p>
      <Link to={SEND_BROADCAST_PATH} className="broadcast-btn broadcast-btn-primary">
        <icons.Send aria-hidden="true" />
        Send Broadcast
      </Link>
    </div>
  );

  const loadFailedState = (
    <div className="broadcast-empty">
      <icons.ExclamationTriangle className="broadcast-empty-icon" aria-hidden="true" />
      <p>{LIST_LOAD_FAILED_MESSAGE}</p>
    </div>
  );

  const hasRows = pager.rows.length > 0;

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div className={data ? "dashboard-content full-contnet" : "dashboard-content"}>
          <h1 className="heading-dash">Broadcasting Center</h1>

          {sentCount !== null && (
            <div className="broadcast-snackbar" role="status">
              <icons.CheckCircleFill className="broadcast-snackbar-icon" aria-hidden="true" />
              <span>
                Broadcast sent to <b>{playersLabel(sentCount)}</b>.
              </span>
              <button type="button" aria-label="Close" onClick={() => setSentCount(null)}>
                <icons.X aria-hidden="true" />
              </button>
            </div>
          )}

          <div className="group_data_table broadcast-card">
            <div className="broadcast-toolbar">
              <div>
                <h2>Broadcasts</h2>
                <div className="broadcast-sub">
                  Email updates sent to your subscribed players, newest first.
                </div>
              </div>
              {(hasRows || pager.loading || pager.failed) && (
                <Link to={SEND_BROADCAST_PATH} className="broadcast-btn broadcast-btn-primary">
                  <icons.Send aria-hidden="true" />
                  Send Broadcast
                </Link>
              )}
            </div>

            {pager.failed && hasRows && (
              <BroadcastAlert variant="warning" role="status">
                {STOPPED_UPDATING_MESSAGE}
              </BroadcastAlert>
            )}

            <DataTable
              columns={columns}
              data={pager.rows}
              keyField="id"
              progressPending={pager.loading && !hasRows}
              persistTableHead
              pointerOnHover
              highlightOnHover
              customStyles={TABLE_STYLES}
              onRowClicked={openDetails}
              conditionalRowStyles={[
                { when: (row) => justSent?.id === row.id, classNames: ["broadcast-row-new"] },
              ]}
              noDataComponent={pager.failed ? loadFailedState : emptyState}
            />
            <ResultsPager
              page={pager.page}
              totalPages={pager.totalPages}
              perPage={pager.perPage}
              pageSizes={pager.pageSizes}
              onPerPageChange={pager.changePerPage}
              onPrevious={pager.previousPage}
              onNext={pager.nextPage}
            />
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
    </>
  );
};
